// API tokens (Smartifakt pattern): list, mint-shown-once, revoke. Lived in
// the old sidebar's More drawer; moved to its own component when the wall's
// Data door became the way in (the cure, Nov 4 2026). Behavior unchanged:
// a minted token is shown exactly once, and every close path clears it.
import React from 'react';
import { API_BASE } from '../config';

export default function TokensModal({ open, onClose, getAuthHeadersOnly }) {
  const [tokenList, setTokenList] = React.useState([]);
  const [tokenName, setTokenName] = React.useState('');
  const [minted, setMinted] = React.useState(null);   // { name, token } — shown exactly once
  const [tokenCopied, setTokenCopied] = React.useState(false);
  const loadTokens = React.useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/api/tokens`, { headers: getAuthHeadersOnly() }); if (r.ok) setTokenList(await r.json()); } catch {}
  }, [getAuthHeadersOnly]);
  const close = () => { setMinted(null); onClose(); };
  const mintToken = async () => {
    const name = tokenName.trim(); if (!name) return;
    const r = await fetch(`${API_BASE}/api/tokens`, { method: 'POST', headers: { ...getAuthHeadersOnly(), 'Content-Type': 'application/json' }, body: JSON.stringify({ name }) });
    if (r.ok) { setMinted(await r.json()); setTokenName(''); loadTokens(); }
  };
  const revokeToken = async (id) => {
    await fetch(`${API_BASE}/api/tokens/${id}`, { method: 'DELETE', headers: getAuthHeadersOnly() });
    loadTokens();
  };
  React.useEffect(() => {
    if (!open) return;
    loadTokens();
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setMinted(null); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,.35)' }} onClick={close}>
      <div className="themed-background rounded-xl shadow-2xl border themed-border w-[460px] max-w-[92vw] p-5 text-left" onClick={e => e.stopPropagation()}>
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-[15px] font-semibold themed-text">API tokens</h3>
          <button onClick={close} className="text-xs themed-text-muted hover:themed-text">esc</button>
        </div>
        <p className="text-[11.5px] themed-text-muted mb-4" style={{ maxWidth: '46ch' }}>
          A token lets your own Claude or scripts read your wall and plant goals, projects and tasks.
          Tokens are additive by design — one can never overwrite, delete, or manage your account.
        </p>

        {minted ? (
          <div className="border themed-border rounded-lg p-3 mb-4">
            <p className="text-[11px] uppercase tracking-widest themed-text-muted font-semibold mb-1.5">“{minted.name}” — copy it now</p>
            <code className="block text-[11px] themed-text break-all mb-2">{minted.token}</code>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => { try { await navigator.clipboard.writeText(minted.token); setTokenCopied(true); setTimeout(() => setTokenCopied(false), 1800); } catch {} }}
                className="px-3 py-1 text-xs themed-button-primary rounded-lg">{tokenCopied ? 'Copied ✓' : 'Copy token'}</button>
              <span className="text-[11px] themed-text-muted">It will never be shown again.</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <input value={tokenName} onChange={e => setTokenName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') mintToken(); e.stopPropagation(); }}
              placeholder="Name it — e.g. Claude Code on my Mac"
              className="flex-1 px-3 py-1.5 text-xs border themed-border rounded-lg themed-background themed-text focus:outline-none" />
            <button onClick={mintToken} disabled={!tokenName.trim()} className="px-3 py-1.5 text-xs themed-button-primary rounded-lg disabled:opacity-40">New token</button>
          </div>
        )}

        {tokenList.length > 0 && (
          <div className="border-t themed-border-light pt-2">
            {tokenList.map(t => (
              <div key={t.id} className="flex items-baseline gap-3 py-1.5 border-b themed-border-light">
                <span className="text-[12px] themed-text flex-1 min-w-0 truncate">{t.name}</span>
                <code className="text-[10.5px] themed-text-muted">{t.prefix}…</code>
                <button onClick={() => revokeToken(t.id)} className="text-[11px] themed-text-muted hover:text-red-600">revoke</button>
              </div>
            ))}
          </div>
        )}
        {tokenList.length === 0 && !minted && (
          <p className="text-[11.5px] themed-text-muted italic">No tokens yet.</p>
        )}
      </div>
    </div>
  );
}
